import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useTransferReasonService from '../../app/services/hooks/useTransferReasonService';
import { TransferReason, TransferReasonFilters } from '../../app/models/TransferReason';
import TransferReasonDataTable from './components/tables/TransferReasonDataTable';
import TransferReasonCreate from './components/TransferReasonCreate';
import TransferReasonEdit from './components/TransferReasonEdit';
import { useHistory } from 'react-router-dom';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Motivos Transferencias',
        url: '/TransferReasons',
        isActive: true
    }
];

const TransferReasons = () => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const {
        fetchingGetTransferReasons,
        getTransferReasons,
        deleteTransferReason,
        activeTransferReason
    } = useTransferReasonService();
    const [transferReasons, setTransferReasons] = useState<TransferReason[]>([]);

    const [transferReasonIdEdit, settransferReasonIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { navigationPage } = useNavigationPage();

    const [transferReasonFilter, setTransferReasonFilter] = useState<TransferReasonFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, []);

    const getAllTransferReasons = () => {
        getTransferReasons(transferReasonFilter, {
            onSuccess: (response: ServiceResponse) => {
                setTransferReasons(response.data.transfer_reasons);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (transferReasonId: number) => {
        setShowingEdit(true);
        settransferReasonIdEdit(transferReasonId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        settransferReasonIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllTransferReasons();
    };

    const destroy = (transferReasonId: number) => {
        const _text = 'Está a punto de eliminar el banco #' + transferReasonId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando banco...');
                        deleteTransferReason(transferReasonId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El banco no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (transferReasonId: number, active: boolean) => {
        activeTransferReason(transferReasonId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Motivo Transferencia" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={
                        <ButtonCreate
                            callbackCreate={showCreate}
                            title="Nuevo Motivo Transferencia"
                        />
                    }
                />
                <TransferReasonDataTable
                    transferReasons={transferReasons}
                    edit={showEdit}
                    destroy={destroy}
                    active={active}
                    loading={fetchingGetTransferReasons}
                    filter={transferReasonFilter}
                    setFilter={setTransferReasonFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Motivo Transferencia"
                    backdrop={true}
                    showFooter={false}
                >
                    <TransferReasonCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Motivo Transferencia"
                    backdrop={true}
                    showFooter={false}
                >
                    <TransferReasonEdit
                        transferReasonId={transferReasonIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default TransferReasons;
